import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiSend, FiCheckCircle, FiBell } = FiIcons;

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const encode = (data) => {
      return Object.keys(data)
        .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
        .join('&');
    };

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "newsletter-form",
          "email": email,
        }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error("Server error");
      }
    } catch (error) {
      console.error("Newsletter error:", error);
      if (window.location.hostname === 'localhost') {
        setStatus('success'); // Simuler succès en local pour l'UI
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    }
  };

  return (
    <section className="py-20 bg-blue-900 dark:bg-blue-950 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute -top-1/2 -left-1/4 w-full h-full border-[60px] border-white rounded-full"
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* MODIFICATION : Transparence (opacity 90%) et Flou (backdrop-blur-md) */}
        <div className="bg-gradient-to-br from-[#01265b]/90 to-[#0245a3]/90 dark:from-gray-800/90 dark:to-gray-900/90 backdrop-blur-md rounded-3xl p-8 md:p-16 shadow-2xl border border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-cyan-400/20 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiBell} className="w-6 h-6 text-cyan-400" />
                </div>
                <span className="text-cyan-400 font-bold tracking-wider uppercase text-sm">Newsletter</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Restez informé des dernières <span className="text-cyan-400">normes ISO</span> et actualités QHSE
              </h2>
              <p className="text-blue-100 text-lg mb-0">
                Inscrivez-vous pour recevoir nos analyses, conseils d'experts et invitations à nos webinaires exclusifs.
              </p>
            </motion.div>

            <motion.div
              initial={{ x: 20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
            >
              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <motion.div
                    key="success"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="bg-green-500/20 border border-green-500/50 rounded-2xl p-8 text-center"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
                      <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Inscription Réussie !</h3>
                    <p className="text-green-100">Merci de votre confiance. Vous recevrez bientôt nos actualités.</p>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    name="newsletter-form"
                    method="POST"
                    data-netlify="true"
                  >
                    <input type="hidden" name="form-name" value="newsletter-form" />
                    <div className="relative">
                      <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        required
                        className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition-all backdrop-blur-sm"
                      />
                    </div>
                    <motion.button
                      type="submit"
                      disabled={status === 'loading'}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 text-blue-900 font-bold rounded-xl transition-all shadow-lg shadow-cyan-500/20 flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <SafeIcon icon={FiSend} className="w-5 h-5" />
                      <span>{status === 'loading' ? 'Inscription...' : "S'abonner à la Newsletter"}</span>
                    </motion.button>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm text-center">Une erreur est survenue. Veuillez réessayer.</p>
                    )}
                    <p className="text-blue-200 text-xs text-center opacity-70">
                      En vous inscrivant, vous acceptez notre politique de confidentialité. Désabonnement possible à tout moment.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;